import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import PageSeo from '../components/PageSeo';
import Newsletters from './Newsletters';
import type { BlogPost } from '../types/blog';
import {
  BLOG_PROOF_METRICS,
  BLOG_SERVICE_PLAYBOOKS,
  BLOG_TOPIC_FILTERS,
  formatBlogDate,
  getBlogPostHref,
  getFeaturedInsightStack,
  getFilteredBlogPosts,
  getPlaybookPosts,
  getTopicBadgeLabel,
  getVisualBadgeClass,
  getFeaturedVisualBadgeClass,
  getVisualGlyph,
  HERO_INSIGHT_NODES,
  type BlogTopicFilterId,
} from '../data/blogPremium';
import '../styles/blog-premium.css';

const PAGE_SIZE = 9;

function FeaturedLeadCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={getBlogPostHref(post)}
      className="blog-premium-featured__lead"
      aria-label={`Read featured article: ${post.title}`}
    >
      <div className={getFeaturedVisualBadgeClass(post)}>
        <span className="blog-premium-featured__badge">{getTopicBadgeLabel(post)}</span>
        <span className="blog-premium-featured__glyph" aria-hidden="true">
          {getVisualGlyph(post.category)}
        </span>
      </div>
      <div className="blog-premium-featured__body">
        <div className="blog-premium-featured__meta">
          <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
          <span>{post.homepageTheme.replace(/-/g, ' ')}</span>
        </div>
        <h3 className="blog-premium-featured__title">{post.title}</h3>
        <p className="blog-premium-featured__excerpt">{post.excerpt}</p>
        <div className="blog-premium-featured__tags">
          {post.tags.slice(0, 4).map((tag) => (
            <span key={tag} className="blog-premium-featured__tag">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

function FeaturedCompactCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={getBlogPostHref(post)}
      className="blog-premium-featured__compact"
      aria-label={`Read featured article: ${post.title}`}
    >
      <div className={getFeaturedVisualBadgeClass(post)}>
        <span className="blog-premium-featured__badge">{getTopicBadgeLabel(post)}</span>
        <span className="blog-premium-featured__glyph" aria-hidden="true">
          {getVisualGlyph(post.category)}
        </span>
      </div>
      <div className="blog-premium-featured__body">
        <div className="blog-premium-featured__meta">
          <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
        </div>
        <h3 className="blog-premium-featured__title">{post.title}</h3>
        <p className="blog-premium-featured__excerpt">{post.excerpt}</p>
      </div>
    </Link>
  );
}

function ArticleCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={getBlogPostHref(post)}
      className="blog-premium-card"
      aria-label={`Read article: ${post.title}`}
    >
      <div className={getVisualBadgeClass(post)}>
        <span className="blog-premium-card__category">{getTopicBadgeLabel(post)}</span>
        <span className="blog-premium-card__glyph" aria-hidden="true">
          {getVisualGlyph(post.category)}
        </span>
      </div>
      <div className="blog-premium-card__body">
        <time className="blog-premium-card__date" dateTime={post.date}>
          {formatBlogDate(post.date)}
        </time>
        <h3 className="blog-premium-card__title">{post.title}</h3>
        <p className="blog-premium-card__excerpt">{post.excerpt}</p>
        <div className="blog-premium-card__chips">
          {post.specializedDelivery.slice(0, 2).map((item) => (
            <span key={item} className="blog-premium-card__chip">
              {item}
            </span>
          ))}
          {post.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="blog-premium-card__chip">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

const Blog = () => {
  const [activeFilter, setActiveFilter] = useState<BlogTopicFilterId>('all');
  const [page, setPage] = useState(0);

  const featuredStack = useMemo(() => getFeaturedInsightStack(), []);

  const filteredPosts = useMemo(
    () => getFilteredBlogPosts(activeFilter),
    [activeFilter],
  );

  const featuredIds = useMemo(() => {
    const ids = new Set<number>();
    ids.add(featuredStack.lead.id);
    featuredStack.secondary.forEach((post) => ids.add(post.id));
    return ids;
  }, [featuredStack]);

  const gridPosts = useMemo(
    () => filteredPosts.filter((post) => !featuredIds.has(post.id)),
    [filteredPosts, featuredIds],
  );

  const totalPages = Math.max(1, Math.ceil(gridPosts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedPosts = gridPosts.slice(
    currentPage * PAGE_SIZE,
    currentPage * PAGE_SIZE + PAGE_SIZE,
  );

  useEffect(() => {
    setPage(0);
  }, [activeFilter]);

  return (
    <>
      <PageSeo path="/blog" />

      <div className="blog-premium">
        <div className="header-spacer" />

        <section className="blog-premium-hero" aria-labelledby="blog-premium-heading">
          <div className="blog-premium__shell">
            <div className="blog-premium-hero__layout">
              <div>
                <p className="blog-premium-hero__kicker">Digital delivery insights · Gautam Buddha Nagar, India</p>
                <h1 id="blog-premium-heading" className="blog-premium-hero__title">
                  Digital delivery insights for web, ecommerce, AI, SEO, and integrations
                </h1>
                <p className="blog-premium-hero__lead">
                  Practical guides on Core Web Vitals, Magento and WooCommerce, CRM and form workflows,
                  GTM and privacy tracking, education search, WordPress Sage, and production-ready delivery
                  for teams shipping reliable websites and platforms.
                </p>
                <div className="blog-premium-hero__actions">
                  <a href="#blog-insights" className="blog-premium-btn blog-premium-btn--primary">
                    Explore insights
                  </a>
                  <Link to="/getintouch" className="blog-premium-btn blog-premium-btn--ghost">
                    Talk to our team
                  </Link>
                </div>
              </div>

              <div className="blog-premium-console" aria-hidden="true">
                {HERO_INSIGHT_NODES.map((node) => (
                  <div key={node} className="blog-premium-console__node">
                    {node}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="blog-premium-proof" aria-labelledby="blog-proof-heading">
          <div className="blog-premium__shell">
            <h2 id="blog-proof-heading" className="sr-only">
              Blog insight metrics
            </h2>
            <div className="blog-premium-proof__grid">
              {BLOG_PROOF_METRICS.map((item) => (
                <article key={item.id} className="blog-premium-proof__card">
                  <span className="blog-premium-proof__value">{item.value}</span>
                  <span className="blog-premium-proof__label">{item.label}</span>
                  <p className="blog-premium-proof__detail">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="blog-premium-section" aria-labelledby="blog-featured-heading">
          <div className="blog-premium__shell">
            <div className="blog-premium-section__header">
              <p className="blog-premium-section__kicker">Featured editorial stack</p>
              <h2 id="blog-featured-heading" className="blog-premium-section__title">
                Lead insights on performance, ecommerce, CRM, analytics, and CMS delivery
              </h2>
              <p className="blog-premium-section__intro">
                Start with our featured production guides — then browse the full insight library by topic
                or service pathway.
              </p>
            </div>

            <div className="blog-premium-featured">
              <FeaturedLeadCard post={featuredStack.lead} />
              <div className="blog-premium-featured__side">
                {featuredStack.secondary.map((post) => (
                  <FeaturedCompactCard key={post.id} post={post} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          id="blog-insights"
          className="blog-premium-section blog-premium-section--dark"
          aria-labelledby="blog-insights-heading"
        >
          <div className="blog-premium__shell">
            <div className="blog-premium-section__header">
              <p className="blog-premium-section__kicker">Insight library</p>
              <h2 id="blog-insights-heading" className="blog-premium-section__title">
                Browse articles by delivery topic
              </h2>
              <p className="blog-premium-section__intro">
                Filter by performance, ecommerce, CRM integrations, analytics, education search, or
                WordPress — each article links to related service pathways.
              </p>
            </div>

            <ul className="blog-premium-filters" aria-label="Filter blog articles by topic">
              {BLOG_TOPIC_FILTERS.map(({ id, label }) => {
                const selected = activeFilter === id;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      aria-pressed={selected}
                      className={`blog-premium-filters__btn${selected ? ' is-active' : ''}`}
                      onClick={() => setActiveFilter(id)}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>

            {pagedPosts.length === 0 ? (
              <p className="blog-premium-grid__empty" role="status">
                No articles match this filter yet. Try another topic or view all insights.
              </p>
            ) : (
              <div className="blog-premium-grid">
                {pagedPosts.map((post) => (
                  <ArticleCard key={post.id} post={post} />
                ))}
              </div>
            )}

            {totalPages > 1 ? (
              <nav className="blog-premium-pagination" aria-label="Blog pagination">
                <button
                  type="button"
                  className="blog-premium-btn blog-premium-btn--secondary"
                  disabled={currentPage === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </button>
                <span className="blog-premium-pagination__status">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  className="blog-premium-btn blog-premium-btn--secondary"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next
                </button>
              </nav>
            ) : null}
          </div>
        </section>

        <section className="blog-premium-section" aria-labelledby="blog-playbooks-heading">
          <div className="blog-premium__shell">
            <div className="blog-premium-section__header">
              <p className="blog-premium-section__kicker">Service playbooks</p>
              <h2 id="blog-playbooks-heading" className="blog-premium-section__title">
                Need help with this? Start with a service pathway
              </h2>
              <p className="blog-premium-section__intro">
                Each playbook groups articles around a delivery journey — web development, ecommerce,
                integrations, analytics, education platforms, and WordPress.
              </p>
            </div>

            <div className="blog-premium-playbooks">
              {BLOG_SERVICE_PLAYBOOKS.map((playbook) => {
                const articles = getPlaybookPosts(playbook).slice(0, 4);
                return (
                  <article key={playbook.id} className="blog-premium-playbook">
                    <h3 className="blog-premium-playbook__title">{playbook.title}</h3>
                    <p className="blog-premium-playbook__summary">{playbook.summary}</p>
                    <ul className="blog-premium-playbook__links">
                      {articles.map((article) => (
                        <li key={article.slug}>
                          <Link
                            to={getBlogPostHref(article)}
                            className="blog-premium-playbook__link"
                          >
                            {article.title}
                            <FaArrowRight aria-hidden="true" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={playbook.serviceHref}
                      className="blog-premium-btn blog-premium-btn--secondary blog-premium-playbook__cta"
                    >
                      {playbook.serviceLabel}
                    </Link>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="blog-premium-cta" aria-labelledby="blog-cta-heading">
          <div className="blog-premium__shell">
            <div className="blog-premium-cta__inner">
              <div>
                <h2 id="blog-cta-heading" className="blog-premium-cta__title">
                  Turn insight into production delivery
                </h2>
                <p className="blog-premium-cta__text">
                  Share your stack, timeline, and integration needs — we will map the right service
                  pathway and delivery team from Gautam Buddha Nagar, India.
                </p>
              </div>
              <Link to="/getintouch" className="blog-premium-btn blog-premium-btn--primary">
                Book a technical consultation
              </Link>
            </div>
          </div>
        </section>

        <Newsletters />
      </div>
    </>
  );
};

export default Blog;
